import { Box, Chip, Tooltip } from '@mui/material';
import { Layers } from '@mui/icons-material';
import { MRT_ColumnDef } from 'material-react-table';
import { Util } from '../../util/Util';
import { NUMBER_FILTER_OPTIONS, TEXT_FILTER_OPTIONS } from '../../util/constants';
import { isVoucherStatus, voucherStatusI18nKey, type VoucherRow } from './voucherTypes';

const statusColor = (status: string): 'success' | 'default' | 'error' | 'warning' => {
  if (status === 'ACTIVE') return 'success';
  if (status === 'REVOKED') return 'error';
  if (status === 'EXPIRED') return 'warning';
  return 'default';
};

type Translate = (key: string, options?: Record<string, unknown>) => string;

export const voucherColumns = (
  t: Translate,
  options?: {
    onCodeClick?: (row: VoucherRow) => void;
    onBatchClick?: (row: VoucherRow) => void;
    batchCounts?: Map<string, number>;
  },
): MRT_ColumnDef<VoucherRow>[] => [
  {
    header: t('common:voucher.code'),
    accessorKey: 'code',
    filterFn: 'contains',
    columnFilterModeOptions: TEXT_FILTER_OPTIONS,
    Cell: ({ cell, row }) => {
      const count = options?.batchCounts?.get(String(row.original.batch || '')) || 0;
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <button
            type="button"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'inherit',
              cursor: 'pointer',
              textDecoration: 'underline',
              font: 'inherit',
            }}
            onClick={() => options?.onCodeClick?.(row.original)}
          >
            {String(cell.getValue() ?? '')}
          </button>
          {count > 1 ? (
            <Tooltip title={t('common:voucher.viewBatch')}>
              <Chip
                size="small"
                variant="outlined"
                icon={<Layers sx={{ fontSize: 14 }} />}
                label={count}
                onClick={(event) => {
                  event.stopPropagation();
                  options?.onBatchClick?.(row.original);
                }}
                sx={{ height: 22, '& .MuiChip-label': { px: 0.6, fontWeight: 600 } }}
              />
            </Tooltip>
          ) : null}
        </Box>
      );
    },
  },
  {
    header: t('common:voucher.status'),
    accessorKey: 'status',
    filterFn: 'equals',
    Cell: ({ cell }) => {
      const raw = cell.getValue();
      const value = isVoucherStatus(raw) ? raw : 'ACTIVE';
      const label = t(voucherStatusI18nKey(value));
      return (
        <Chip
          size="small"
          color={statusColor(value)}
          label={label.startsWith('common:') ? value : label}
        />
      );
    },
  },
  {
    header: t('common:voucher.batch'),
    accessorKey: 'batch',
    Cell: ({ cell }) => {
      const value = String(cell.getValue() ?? '');
      return value ? `${value.slice(0, 8)}…` : '—';
    },
  },
  {
    header: t('common:voucher.type'),
    accessorKey: 'type',
    filterFn: 'contains',
    columnFilterModeOptions: TEXT_FILTER_OPTIONS,
    Cell: ({ cell }) => {
      const value = String(cell.getValue() ?? '');
      const label = t(`common:voucher.types.${value}`);
      return label.startsWith('common:') ? value : label;
    },
  },
  {
    header: t('common:voucher.issuer'),
    accessorKey: 'issuer',
    filterFn: 'contains',
    columnFilterModeOptions: TEXT_FILTER_OPTIONS,
  },
  {
    header: t('common:voucher.issuee'),
    accessorKey: 'issuee',
    Cell: ({ cell }) => String(cell.getValue() ?? '') || '—',
  },
  {
    header: t('common:tables.amount'),
    accessorKey: 'amount',
    Cell: ({ cell }) => Util.formatNumber(Number(cell.getValue() || 0)),
    filterFn: 'greaterThanOrEqualTo',
    columnFilterModeOptions: NUMBER_FILTER_OPTIONS,
  },
  {
    header: t('common:voucher.expiry'),
    accessorKey: 'expiry',
    Cell: ({ cell }) => {
      const value = String(cell.getValue() ?? '');
      return value ? Util.formatDate(value, 'medium') : '—';
    },
  },
  {
    header: t('common:voucher.pos'),
    accessorKey: 'pos_name',
    Cell: ({ cell }) => String(cell.getValue() ?? '') || '—',
  },
  {
    header: t('common:tables.mode'),
    accessorKey: 'mode_name',
    Cell: ({ cell }) => String(cell.getValue() ?? '') || '—',
  },
  {
    header: t('common:tables.user'),
    accessorKey: 'user',
  },
  {
    header: t('common:voucher.issued'),
    accessorKey: 'issued',
    Cell: ({ cell }) => {
      const value = String(cell.getValue() ?? '');
      return value ? Util.formatDate(value, 'medium') : '—';
    },
  },
];
